import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PxGrid from 'components/layout/PxGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import React, { useEffect, useState } from "react";
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { addTestKitReceptionOption, editTestKitReceptionOption } from 'services/labCentralServices';
import { InterventionBookingStatus } from 'ts-types/api.enums';
import { UpsertTestKitReceptionOptionDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps {

  testKitReceptionOptionId?: number,
  testKitReceptionOption?: UpsertTestKitReceptionOptionDto,
  onCancel: () => void,
  onSave: () => void
}

let initialValues: Partial<UpsertTestKitReceptionOptionDto> = {};

const UpsertTestKitReceptionOptionForm = (props: Props) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
  const {getMessage, onCancel, onSave, testKitReceptionOptionId, testKitReceptionOption} = props;

  const [testKitLoaded, setTestKitLoaded] = useState(false as boolean);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);


  const interventionStatusOptions = stringArrayToDropdownOptionArray(
      Object.values(InterventionBookingStatus), getMessage, "enum.interventionBookingStatus");

  const [interventionBookingStatusOptions, setInterventionBookingStatusOptions] = useState(interventionStatusOptions as DropdownOption[]);

  useEffect(() => {

    initialValues = {
      description: "",
      descriptionEn: "",
      descriptionFr: "",
      descriptionIt: "",
      status: InterventionBookingStatus.COMPLETED,
      active: true
    }

    if (testKitReceptionOptionId) {
      initialValues = {...testKitReceptionOption}
    }

    setTestKitLoaded(true)

    // eslint-disable-next-line
  }, []);

  const handleSubmit = async (values: Partial<UpsertTestKitReceptionOptionDto>) => {

    if (testKitReceptionOptionId) {
      await editTestKitReceptionOption(testKitReceptionOptionId, values, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => handleError(e.response.data))
    } else {
      await addTestKitReceptionOption(values, cancelTokenSource)
          .then(onSave)
          .catch((e: any) => handleError(e.response.data))
    }
  }

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.unknownTestKitReceptionOption
    ];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const renderForm = ({submitting, handleSubmit}: FormRenderProps): React.ReactNode => {

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <PxGrid>
              {errorMessage &&
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                        {errorMessage}
                      </StyledErrorMessage>
                    </Grid.Column>
                  </Grid.Row>
              }
              <Grid.Row>
                <Grid.Column>
                  <Grid.Column width={8} textAlign="right">
                    <Field
                        name="active"
                        component={CheckBox}
                        label={getMessage("active")}
                        toggle
                    />
                  </Grid.Column>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  {getMessage("labCentral.testKitReceptionOption")}
                </Grid.Column>
                <Grid.Column width={8}>
                  <Field name="description"
                         component={Input}
                         autoFocus
                         fluid
                         validate={required}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  {getMessage("labCentral.testKitReceptionOptionEn")}
                </Grid.Column>
                <Grid.Column width={8}>
                  <Field name="descriptionEn"
                         component={Input}
                         fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  {getMessage("labCentral.testKitReceptionOptionFr")}
                </Grid.Column>
                <Grid.Column width={8}>
                  <Field name="descriptionFr"
                         component={Input}
                         fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  {getMessage("labCentral.testKitReceptionOptionIt")}
                </Grid.Column>
                <Grid.Column width={8}>
                  <Field name="descriptionIt"
                         component={Input}
                         fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={8}>
                  {getMessage("status")}
                </Grid.Column>
                <Grid.Column width={8}>
                  <Field
                      id="status"
                      name="status"
                      options={interventionBookingStatusOptions}
                      component={Select}
                      validate={required}
                      fluid
                  />
                </Grid.Column>
              </Grid.Row>
            </PxGrid>
            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>
                {getMessage("button.cancel")}
              </Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  }

  if (!testKitLoaded) {
    return <LoaderComponent message={getMessage("loading")}/>
  }

  return (

      <FinalForm
          onSubmit={handleSubmit}
          initialValues={initialValues}
          subscription={{values: true, pristine: true, submitting: true}}
          render={renderForm}
      />
  );

}

export default withMessages(withAuthContext(UpsertTestKitReceptionOptionForm));
